import { AssignmentTurnedInOutlined } from "@mui/icons-material";
import { Link, Stack, SxProps, Tooltip, Typography, useTheme } from "@mui/material";
import { ActionItemDrawerContent, ContextSyncAlert, useDrawer } from "src/components";
import { Links } from "src/constants";
import { ContextActionItemDto, ContextPathDto } from "src/http";

interface IProps {
  context: ContextActionItemDto | ContextPathDto;
  showIcon?: boolean;
  disableLink?: boolean;
  hideTeamName?: boolean;
  condensed?: boolean;
  openDrawerOnClick?: boolean;
  sx?: SxProps;
}

const ContextLabel = (props: IProps) => {
  const { context } = props;
  const { showDrawer } = useDrawer();
  const theme = useTheme();

  const isConnectionChanged = context?.updatedVersion && context.description !== context.updatedVersion.description;
  const isConnectionDeleted = context?.updatedVersion && context.updatedVersion.isDeleted;

  const showSyncAlert = (isConnectionChanged || isConnectionDeleted) && !props.disableLink;

  function handleActionItemClicked() {
    showDrawer({
      content: <ActionItemDrawerContent actionItemGuid={context.actionItemGuid} />,
    });
  }

  return (
    <Stack
      direction="row"
      spacing={0.25}
      sx={{
        alignItems: "center",
        flexGrow: 1,
        minWidth: "0px",
        width: "100%",
        ...props.sx
      }}>
      {props.showIcon && <AssignmentTurnedInOutlined fontSize={props.condensed ? "small" : "medium"} />}
      <Tooltip
        placement="top-start"
        enterDelay={1000}
        enterNextDelay={1000}
        leaveDelay={500}
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: props.disableLink ? "#f6f6f6" : undefined,
            },
          },
        }}
        title={
          <Typography variant="body1">
            {!props.disableLink ? (
              <>
                {context.team && (
                  <>
                    <Link
                      href={Links.TeamStrategicMap(context.team.slug)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                    >
                      {context.team.name}
                    </Link>
                    {" : "}
                  </>
                )}

                {isConnectionDeleted ? (
                  <>{context.description}</>
                ) : (
                  <Link
                    href={Links.ActionItemDetail(context.actionItemGuid)}
                    onClick={(e) => {
                      if (props.openDrawerOnClick) {
                        e.preventDefault();
                        handleActionItemClicked();
                      }
                      e.stopPropagation();
                    }}
                  >
                    {context.description}
                  </Link>
                )}
              </>
            ) : (
              context.description
            )}
          </Typography>
        }
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}>
          <>
            {!props.disableLink && context.team && !props.hideTeamName && (
              <>
                <Link noWrap href={Links.Team(context.team.slug)} onClick={(e) => e.stopPropagation()}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontSize: props.condensed ? 12 : "unset", textTransform: "uppercase" }}
                  >
                    {context.team.name}
                  </Typography>
                </Link>

                <Typography
                  variant="body2"
                  sx={{ pb: 0.1, fontSize: props.condensed ? 12 : "unset", mx: 0.25 }}
                  color={theme.palette.grey[300]}
                >
                  :
                </Typography>
              </>
            )}

            {!props.disableLink && !isConnectionDeleted ? (
              <Link
                noWrap
                href={Links.ActionItemDetail(context.actionItemGuid)}
                onClick={(e) => {
                  if (props.openDrawerOnClick) {
                    e.preventDefault();
                    handleActionItemClicked();
                  }
                  e.stopPropagation();
                }}
              >
                <Typography variant="body1" noWrap sx={{ fontSize: props.condensed ? 12 : "unset" }}>
                  {context.description}
                </Typography>
              </Link>
            ) : (
              <Typography
                variant="body1"
                noWrap
                sx={{ color: theme.palette.text.secondary, fontSize: props.condensed ? 12 : "unset" }}
              >
                {context.description}
              </Typography>
            )}
          </>
        </Stack>
      </Tooltip>

      {showSyncAlert && context.updatedVersion && (
        <ContextSyncAlert previousDescription={context.description} updatedContext={context.updatedVersion} />
      )}
    </Stack>
  );
};

export { ContextLabel };
